import type { ReleaseManifest } from '@/shared/release-manifest';

export const releaseTransportHeaders = Object.freeze({
    revision: 'x-marketplace-release-revision',
    protocolVersion: 'x-marketplace-release-protocol-version',
    minSupportedFrontendProtocolVersion: 'x-marketplace-release-min-supported-frontend-protocol-version',
});

export const releaseTransportBroadcastChannelName = 'marketplace-reviews:release-manifest';

const readHeaderValue = (headers: unknown, headerName: string): string | null => {
    if (!headers || typeof headers !== 'object') {
        return null;
    }

    if (headers instanceof Headers) {
        return headers.get(headerName);
    }

    const maybeHeadersWithGet = headers as { get?: (name: string) => string | null | undefined };
    if (typeof maybeHeadersWithGet.get === 'function') {
        return maybeHeadersWithGet.get(headerName) ?? maybeHeadersWithGet.get(headerName.toLowerCase()) ?? null;
    }

    const normalizedHeaderName = headerName.toLowerCase();
    const entries = Object.entries(headers);
    for (const [key, rawValue] of entries) {
        if (key.toLowerCase() !== normalizedHeaderName) {
            continue;
        }

        return typeof rawValue === 'string' ? rawValue : rawValue == null ? null : String(rawValue);
    }

    return null;
};

const readPositiveInteger = (rawValue: string | null): number | null => {
    if (!rawValue || !/^\d+$/.test(rawValue)) {
        return null;
    }

    const parsed = Number(rawValue);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return null;
    }

    return parsed;
};

export const buildReleaseTransportHeaders = (manifest: ReleaseManifest): Record<string, string> => ({
    [releaseTransportHeaders.revision]: manifest.revision,
    [releaseTransportHeaders.protocolVersion]: String(manifest.protocolVersion),
    [releaseTransportHeaders.minSupportedFrontendProtocolVersion]: String(manifest.minSupportedFrontendProtocolVersion),
});

export const readReleaseManifestFromHeaderBag = (headers: unknown): ReleaseManifest | null => {
    const revision = readHeaderValue(headers, releaseTransportHeaders.revision)?.trim() ?? '';
    const protocolVersion = readPositiveInteger(readHeaderValue(headers, releaseTransportHeaders.protocolVersion));
    const minSupportedFrontendProtocolVersion = readPositiveInteger(
        readHeaderValue(headers, releaseTransportHeaders.minSupportedFrontendProtocolVersion),
    );

    if (!/^[0-9a-f]{40}$/i.test(revision) || protocolVersion === null || minSupportedFrontendProtocolVersion === null) {
        return null;
    }

    return Object.freeze({
        revision,
        protocolVersion,
        minSupportedFrontendProtocolVersion,
    });
};
